import type { FileUIPart } from "ai";
import { apiFetch } from "@/lib/api-client";
import { uploadAttachWithProgress } from "@/lib/upload/attach-upload-client";
import { deriveUploadKind, getFileExtension, isUploadFileAllowed, type UploadKind } from "@/lib/upload/kinds";
import type { AttachUploadRulesRecord } from "@/components/admin/attach-picker/types";
import { modelAcceptsAttachment, type ModelFeatureProfile } from "./model-features";
import { attachmentLimitIssue, type AiInputLimitIssue } from "./input-limits";

export type ChatUploadedAttachment = {
  attachId: string;
  mediaType: string;
  url: string;
  filename: string;
};

type UploadMessages = { failed: string; network: string };

export async function uploadChatFile(file: File | FileUIPart, messages: UploadMessages): Promise<ChatUploadedAttachment> {
  let raw: File;
  if (file instanceof File) raw = file;
  else {
    const blob = await (await fetch(file.url)).blob();
    raw = new File([blob], file.filename || "file", { type: file.mediaType || blob.type });
  }
  const record = await uploadAttachWithProgress({
    file: raw,
    kind: deriveUploadKind(getFileExtension(raw.name)),
    groupId: "default",
    module: "ai",
    messages,
  });
  return { attachId: record.id, mediaType: record.mimeType ?? raw.type, url: record.url, filename: record.originalName || raw.name };
}

/** Validate the whole drop before uploading; report each successful file immediately. */
export async function uploadChatDroppedFiles(input: {
  files: File[];
  profile: ModelFeatureProfile;
  existingFiles?: readonly { mediaType: string }[];
  messages: UploadMessages & {
    unsupported: string;
    countLimit: (issue: AiInputLimitIssue) => string;
    invalidFile: (name: string, reason: "empty" | "tooLarge" | "extension", rules: AttachUploadRulesRecord) => string;
  };
  onUploaded: (file: ChatUploadedAttachment) => void;
}) {
  const { files, profile, messages } = input;
  const issue = attachmentLimitIssue(profile.inputLimits, [...(input.existingFiles ?? []), ...files.map(file => ({ mediaType: file.type }))]);
  if (issue) throw new Error(messages.countLimit(issue));
  if (files.some(file => !modelAcceptsAttachment(profile, file.type, file.name))) throw new Error(messages.unsupported);
  const rules = new Map<UploadKind, AttachUploadRulesRecord>();
  for (const file of files) {
    const kind = deriveUploadKind(getFileExtension(file.name));
    if (!rules.has(kind)) {
      const params = new URLSearchParams({ kind, module: "ai" });
      rules.set(kind, (await apiFetch<{ rules: AttachUploadRulesRecord }>(`/api/attach/upload/rules?${params}`)).rules);
    }
    const rule = rules.get(kind)!;
    const check = isUploadFileAllowed(file.name, file.size, rule);
    if (!check.ok) throw new Error(messages.invalidFile(file.name, check.reason, rule));
  }
  for (const file of files) input.onUploaded(await uploadChatFile(file, messages));
}
